import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from '@mui/material';

const normalizeTargetSchema = (targetSchema) => {
  if (!targetSchema) return [];
  if (Array.isArray(targetSchema)) return targetSchema;
  return [{ tableName: targetSchema, columns: [], id: targetSchema }];
};

const DirectMappingDialog = ({
  open,
  onClose,
  sources = [],
  targetSchema,
  fetchedHeader = [],
  initialValues = null,
  editIndex = null,
  existingMappings = [],
  onSave,
  reviewMode = false,
  onReject,
  onApprove,
}) => {
  const targets = useMemo(() => normalizeTargetSchema(targetSchema), [targetSchema]);

  const [sourceTable, setSourceTable] = useState(initialValues?.sourceTable || '');
  const [sourceCol, setSourceCol] = useState(initialValues?.sourceCol || '');
  const [targetTable, setTargetTable] = useState(initialValues?.targetTable || '');
  const [targetCol, setTargetCol] = useState(initialValues?.targetCol || '');
  const prevOpenRef = useRef(false);
  const editIndexRef = useRef(null);

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      editIndexRef.current = editIndex;
      setSourceTable(initialValues?.sourceTable || '');
      setSourceCol(initialValues?.sourceCol || '');
      setTargetTable(initialValues?.targetTable || '');
      setTargetCol(initialValues?.targetCol || '');
    }
    prevOpenRef.current = open;
  }, [open, initialValues, editIndex]);

  const sourceTableOptions = useMemo(
    () => sources.map((s) => ({ value: s.name || s.id, label: s.name || s.id })),
    [sources]
  );

  const sourceColOptions = useMemo(() => {
    if (!sourceTable) return [];
    const src = sources.find((s) => (s.name || s.id) === sourceTable);
    const cols = src?.columns || [];
    return Array.isArray(cols) ? cols.map((c) => ({ value: c, label: c })) : [];
  }, [sources, sourceTable]);

  const targetTableOptions = useMemo(
    () => targets.map((t) => ({ value: t.tableName || t.id, label: t.tableName || t.id })),
    [targets]
  );

  /** Target columns already mapped (from WebSocket or state) for the selected target table */
  const mappedTargetColsForTable = useMemo(() => {
    if (!targetTable || !Array.isArray(existingMappings)) return new Set();
    return new Set(
      existingMappings
        .filter((m) => (m.destination_name || m.destinationName) === targetTable)
        .map((m) => (m.target_column || m.targetColumn || '').trim())
        .filter(Boolean)
    );
  }, [targetTable, existingMappings]);

  const targetColOptions = useMemo(() => {
    if (!targetTable) return [];
    const target = targets.find((t) => (t.tableName || t.id) === targetTable);
    const normalizeCol = (c) => {
      if (c == null) return '';
      if (typeof c === 'string') return c;
      return c.column_name ?? c.name ?? c.columnName ?? '';
    };
    let opts = [];
    if (target?.columns?.length) {
      opts = target.columns.map((c) => {
        const name = normalizeCol(c);
        return { value: name, label: name };
      }).filter((opt) => opt.value != null && String(opt.value).trim() !== '');
    }
    if (opts.length === 0) {
      opts = (fetchedHeader || [])
        .filter((h) => h.sourceTable === targetTable)
        .map((h) => ({ value: h.columnName ?? h.column_name, label: h.columnName ?? h.column_name }))
        .filter((opt) => opt.value != null && opt.value !== '');
    }
    // Show only columns not yet mapped; keep current selection when editing so it stays visible
    const currentSelection = initialValues?.targetCol ?? targetCol;
    return opts.filter(
      (opt) => !mappedTargetColsForTable.has(opt.value) || opt.value === currentSelection
    );
  }, [targets, targetTable, fetchedHeader, mappedTargetColsForTable, initialValues?.targetCol, targetCol]);

  const handleClose = () => {
    setSourceTable('');
    setSourceCol('');
    setTargetTable('');
    setTargetCol('');
    onClose();
  };

  const handleSave = () => {
    if (!sourceTable || !sourceCol || !targetTable || !targetCol) return;
    onSave?.({ sourceTable, sourceCol, targetTable, targetCol }, editIndexRef.current);
    handleClose();
  };

  const canSave = sourceTable && sourceCol && targetTable && targetCol;

  const selectSx = {
    minWidth: 160,
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: '#fafafa',
      '&:hover': { backgroundColor: '#f5f5f5' },
      '&.Mui-focused': { backgroundColor: '#fff' },
    },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: '1.25rem',
          color: '#1f2937',
          borderBottom: '1px solid #e5e7eb',
          py: 2,
          px: 3,
          backgroundColor: '#fafafa',
        }}
      >
        Direct Mapping
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 3, backgroundColor: '#fff' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.08em', fontSize: '0.7rem', display: 'block', mb: 1.5 }}>
              Source
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" fullWidth sx={selectSx}>
                <InputLabel id="direct-source-table">Source table</InputLabel>
                <Select
                  labelId="direct-source-table"
                  value={sourceTable}
                  label="Source table"
                  onChange={(e) => {
                    setSourceTable(e.target.value);
                    setSourceCol('');
                  }}
                  disabled={reviewMode}
                  MenuProps={{ PaperProps: { sx: { borderRadius: 2, mt: 1.5 } } }}
                >
                  {sourceTableOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth sx={selectSx}>
                <InputLabel id="direct-source-col">Source col</InputLabel>
                <Select
                  labelId="direct-source-col"
                  value={sourceCol}
                  label="Source col"
                  onChange={(e) => setSourceCol(e.target.value)}
                  disabled={reviewMode || !sourceTable}
                  MenuProps={{ PaperProps: { sx: { borderRadius: 2, mt: 1.5 } } }}
                >
                  {sourceColOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
          <Box>
            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.08em', fontSize: '0.7rem', display: 'block', mb: 1.5 }}>
              Target
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" fullWidth sx={selectSx}>
                <InputLabel id="direct-target-table">Target table</InputLabel>
                <Select
                  labelId="direct-target-table"
                  value={targetTable}
                  label="Target table"
                  onChange={(e) => {
                    setTargetTable(e.target.value);
                    setTargetCol('');
                  }}
                  disabled={reviewMode}
                  MenuProps={{ PaperProps: { sx: { borderRadius: 2, mt: 1.5 } } }}
                >
                  {targetTableOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth sx={selectSx}>
                <InputLabel id="direct-target-col">Target col</InputLabel>
                <Select
                  labelId="direct-target-col"
                  value={targetCol ?? ''}
                  label="Target col"
                  onChange={(e) => setTargetCol(e.target.value)}
                  disabled={reviewMode || !targetTable}
                  MenuProps={{
                    PaperProps: { sx: { borderRadius: 2, mt: 1.5 } },
                    disableScrollLock: true,
                  }}
                >
                  {targetTable && targetColOptions.length === 0 ? (
                    <MenuItem value="" disabled>
                      No columns available
                    </MenuItem>
                  ) : (
                    targetColOptions.map((opt, idx) => (
                      <MenuItem key={`targetcol-${idx}-${opt.value}`} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1.5,
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#fafafa',
        }}
      >
        {reviewMode ? (
          <>
            <Button
              onClick={onReject}
              variant="contained"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                px: 3,
                bgcolor: '#dc2626',
                color: '#fff',
                '&:hover': { bgcolor: '#b91c1c' },
              }}
            >
              Reject
            </Button>
            <Button
              onClick={onApprove}
              variant="contained"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                px: 3,
                bgcolor: '#16a34a',
                color: '#fff',
                '&:hover': { bgcolor: '#15803d' },
              }}
            >
              Approve
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={handleClose}
              variant="outlined"
              color="inherit"
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 2.5 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={!canSave}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                px: 3,
                bgcolor: '#00043c',
                color: '#fff',
                boxShadow: '0 2px 8px rgba(0, 4, 60, 0.35)',
                '&:hover': {
                  bgcolor: '#000335',
                  boxShadow: '0 4px 12px rgba(0, 4, 60, 0.4)',
                },
              }}
            >
              Save
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DirectMappingDialog;
